import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

export interface Project {
  id: string;
  name: string;
  description: string;
  ownerUid: string;
  subscriberCount: number;
  allowedDomains: string[];
  createdAt: Date | null;
}

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface Subscriber {
  id: string;
  email: string;
  subscribedAt: Date | null;
  utm?: UtmParams;
  referrer?: string;
}

/** Fetch all projects owned by the given UID. */
export async function listProjects(ownerUid: string): Promise<Project[]> {
  const q = query(
    collection(db, 'projects'),
    where('ownerUid', '==', ownerUid),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs
    .filter((d) => !d.data()['archived'])
    .map((d) => {
      const data = d.data();
      return {
        id: d.id,
        name: data['name'] as string,
        description: data['description'] as string,
        ownerUid: data['ownerUid'] as string,
        subscriberCount: (data['subscriberCount'] as number) ?? 0,
        allowedDomains: (data['allowedDomains'] as string[] | undefined) ?? [],
        createdAt: (data['createdAt'] as Timestamp | null)?.toDate() ?? null,
      };
    });
}

/** Create a new project. */
export async function createProject(
  ownerUid: string,
  name: string,
  description: string,
): Promise<string> {
  const ref = await addDoc(collection(db, 'projects'), {
    name: name.trim(),
    description: description.trim(),
    ownerUid,
    subscriberCount: 0,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

/** Archive a project (soft delete). */
export async function deleteProject(projectId: string): Promise<void> {
  await updateDoc(doc(db, 'projects', projectId), {
    archived: true,
    archivedAt: new Date().toISOString(),
  });
}

/** Fetch subscriber list for a project. */
export async function listSubscribers(projectId: string): Promise<Subscriber[]> {
  const snap = await getDocs(collection(db, 'projects', projectId, 'subscribers'));
  return snap.docs.map((d) => ({
    id: d.id,
    email: d.data()['email'] as string,
    subscribedAt: (d.data()['subscribedAt'] as Timestamp | null)?.toDate() ?? null,
    utm: (d.data()['utm'] as UtmParams | undefined) ?? undefined,
    referrer: (d.data()['referrer'] as string | undefined) ?? undefined,
  }));
}

/** Fetch a single project by its ID. */
export async function getProject(projectId: string): Promise<Project> {
  const snap = await getDoc(doc(db, 'projects', projectId));
  if (!snap.exists()) {
    throw new Error('Project not found');
  }
  const data = snap.data();
  return {
    id: snap.id,
    name: data['name'] as string,
    description: data['description'] as string,
    ownerUid: data['ownerUid'] as string,
    subscriberCount: (data['subscriberCount'] as number) ?? 0,
    allowedDomains: (data['allowedDomains'] as string[] | undefined) ?? [],
    createdAt: (data['createdAt'] as Timestamp | null)?.toDate() ?? null,
  };
}
/** Update the list of allowed email domains for a project. */
export async function updateProjectDomains(
  user: { getIdToken: () => Promise<string> },
  projectId: string,
  domains: string[],
): Promise<string[]> {
  const token = await user.getIdToken();
  const { API_URL } = await import('../config');
  const res = await fetch(`${API_URL}/projects/${projectId}/domains`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ allowedDomains: domains }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? 'Failed to update allowed domains.');
  }
  const data = (await res.json()) as { allowedDomains: string[] };
  return data.allowedDomains;
}
